import * as THREE from 'three';
import { ShipView } from '../game/view.js';
import { Settings } from '../persist.js';

/**
 * Chase camera with speed-reactive FOV, boost kick, damped follow, shake,
 * look-back, and orbit/spectate modes.
 */
export class ChaseCamera {
  camera: THREE.PerspectiveCamera;
  private pos = new THREE.Vector3();
  private lookTarget = new THREE.Vector3();
  private shake = 0;
  private shakeTime = 0;
  private fovKick = 0;
  private mode: 'chase' | 'orbit' | 'podium' = 'chase';
  private orbitAngle = 0;
  private initialized = false;

  private tmpUp = new THREE.Vector3();
  private tmpFwd = new THREE.Vector3();
  private tmpTarget = new THREE.Vector3();
  private tmpQuat = new THREE.Quaternion();

  constructor(private settings: Settings) {
    this.camera = new THREE.PerspectiveCamera(settings.baseFov, 16 / 9, 0.1, 6000);
  }

  setMode(mode: 'chase' | 'orbit' | 'podium'): void {
    this.mode = mode;
  }

  addShake(intensity: number): void {
    this.shake = Math.min(1.2, this.shake + intensity * this.settings.cameraShake);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  snapTo(ship: ShipView): void {
    this.initialized = false;
    this.update(ship, 0.1, false, 0);
  }

  update(ship: ShipView, dt: number, lookBack: boolean, speedNorm: number): void {
    const reduced = this.settings.reducedMotion;
    this.tmpQuat.set(ship.quat.x, ship.quat.y, ship.quat.z, ship.quat.w);
    this.tmpUp.set(0, 1, 0).applyQuaternion(this.tmpQuat);
    this.tmpFwd.set(0, 0, 1).applyQuaternion(this.tmpQuat);

    if (this.mode === 'orbit' || this.mode === 'podium') {
      this.orbitAngle += dt * (this.mode === 'podium' ? 0.25 : 0.5);
      const r = this.mode === 'podium' ? 9 : 14;
      const h = this.mode === 'podium' ? 3.2 : 5;
      this.tmpTarget.set(
        ship.pos.x + Math.cos(this.orbitAngle) * r,
        ship.pos.y + h,
        ship.pos.z + Math.sin(this.orbitAngle) * r
      );
      this.pos.lerp(this.tmpTarget, this.initialized ? 1 - Math.exp(-4 * dt) : 1);
      this.camera.position.copy(this.pos);
      this.camera.lookAt(ship.pos.x, ship.pos.y + 1, ship.pos.z);
      this.camera.fov = this.settings.baseFov * 0.9;
      this.camera.updateProjectionMatrix();
      this.initialized = true;
      return;
    }

    // chase: sit behind and above in the ship's frame
    const back = lookBack ? -1 : 1;
    const dist = 8.2 + speedNorm * 2.2;
    const height = 2.6 + speedNorm * 0.4;
    this.tmpTarget
      .set(ship.pos.x, ship.pos.y, ship.pos.z)
      .addScaledVector(this.tmpFwd, -dist * back)
      .addScaledVector(this.tmpUp, height);

    if (!this.initialized) {
      this.pos.copy(this.tmpTarget);
      this.initialized = true;
    } else {
      // stiffer at higher speed so the ship doesn't outrun the camera
      const stiffness = 7 + speedNorm * 9;
      this.pos.lerp(this.tmpTarget, 1 - Math.exp(-stiffness * dt));
    }

    // look ahead of the ship
    this.lookTarget
      .set(ship.pos.x, ship.pos.y, ship.pos.z)
      .addScaledVector(this.tmpFwd, 14 * back)
      .addScaledVector(this.tmpUp, 1.2);

    this.camera.position.copy(this.pos);

    // shake
    if (this.shake > 0.003 && !reduced) {
      this.shakeTime += dt * 31;
      const s = this.shake * 0.24;
      this.camera.position.x += Math.sin(this.shakeTime * 1.3) * s;
      this.camera.position.y += Math.sin(this.shakeTime * 1.7 + 1.3) * s * 0.7;
      this.camera.position.z += Math.sin(this.shakeTime * 1.1 + 2.6) * s;
      this.shake *= Math.exp(-5.5 * dt);
    } else {
      this.shake = 0;
    }

    this.camera.up.copy(this.tmpUp);
    this.camera.lookAt(this.lookTarget);

    // FOV: base + speed + boost kick
    const targetKick = (ship.boosting ? 14 : 0) + ship.draftFactor * 5;
    this.fovKick += (targetKick - this.fovKick) * (1 - Math.exp(-6 * dt));
    const speedFov = reduced ? speedNorm * 4 : speedNorm * 11;
    const fov = this.settings.baseFov + speedFov + (reduced ? this.fovKick * 0.3 : this.fovKick);
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }
}
